<script setup lang="ts">
/**
 * Tooltip riche d'une recherche — parité avec BuildingTooltipContent (cf wiki
 * §24.7-ter) : image + description + ce que ça AMÉLIORE (effets, avec ×facteur
 * au niveau max) + coût du prochain niveau (flouz + ressources) + courbe SVG du
 * bonus par niveau + ce que ça DÉBLOQUE (bâtiments / vaisseaux / recherches).
 */
import { computed } from 'vue';
import type { ResearchNodeView } from '@/types/research';
import ResourceCostList from '@/components/shared/ResourceCostList.vue';
import PrerequisiteList from '@/components/shared/PrerequisiteList.vue';
import LevelUpCurve from '@/components/shared/LevelUpCurve.vue';
import type { CostLine, PrereqLine } from '@/types/constructible';

const props = defineProps<{
  node: ResearchNodeView;
  /** Solde flouz du joueur (souligne le coût en rouge si insuffisant). */
  playerFlouz?: number;
  /** Stock dispo par ressource (souligne en rouge si insuffisant). */
  availableByResourceId?: Record<number, number>;
  /** Niveau du Laboratoire central de la planète (labo avancé, paliers hauts). */
  laboCentralLevel?: number | null;
  /** Niveau du Centre de recherche de la planète (labo de base, paliers bas). */
  centreLevel?: number | null;
}>();

// Labo producteur de CETTE recherche (selon sa ProductionCategory) : nom à afficher
// + niveau actuel sur la planète.
const labBuilding = computed(() => {
  const isCentral = props.node.productionCategory === 'LaboratoireCentral';
  return {
    label: isCentral ? 'Laboratoire central' : 'Centre de recherche',
    current: isCentral ? props.laboCentralLevel : props.centreLevel,
  };
});
// Manquant si le labo producteur n'atteint pas le NIVEAU requis (RequiredBuildingLevel).
// current null/undefined = vue globale → neutre.
const buildingMissing = computed(() =>
  (props.node.requiredBuildingLevel ?? 0) > 0
  && labBuilding.value.current != null
  && labBuilding.value.current < (props.node.requiredBuildingLevel ?? 0));

function humanDuration(s: number): string {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  return `${Math.floor(s / 86400)}j ${Math.floor((s % 86400) / 3600)}h`;
}

const n = computed(() => props.node);
// Dossier de représentation (wiki §24.7-ter.8) : image de bâtiment réutilisée ou symbole.
const imgFolder = computed(() => props.node.imageFolder || 'research_symbols');
const maxed = computed(() => n.value.currentLevel >= n.value.maxLevel);
const levelupFactorAtMax = computed(() =>
  Math.pow(1 + n.value.levelupPct / 100, Math.max(0, n.value.maxLevel - 1)));

// Courbe par niveau — composant PARTAGÉ LevelUpCurve (style unifié bâtiment).
// Valeur au niveau N = ×(1 + levelupPct/100)^(N-1), sur tous les niveaux ; le
// prochain niveau est mis en avant.
const curveData = computed(() =>
  Array.from({ length: n.value.maxLevel }, (_, i) => ({
    level: i + 1,
    value: Math.pow(1 + n.value.levelupPct / 100, i),
  })),
);
const nextCurveLevel = computed(() => Math.min(n.value.maxLevel, n.value.currentLevel + 1));
const hasCurve = computed(() => n.value.levelupPct > 0 && n.value.maxLevel > 1);

const u = computed(() => n.value.unlocks ?? { buildings: [], ships: [], researches: [] });
const hasUnlocks = computed(() =>
  u.value.buildings.length + u.value.ships.length + u.value.researches.length > 0);

// Coût du prochain niveau -> composant partagé (flouz INCLUS ici : la recherche
// est le puits flouz du joueur, contrairement aux bâtiments/vaisseaux).
const costGeneric = computed<CostLine[]>(() => {
  const c = n.value.nextLevelCost;
  if (!c) return [];
  const lines: CostLine[] = [];
  if (c.flouz > 0) lines.push({ code: 'flouz', label: 'Flouz', amount: c.flouz, available: props.playerFlouz });
  for (const r of c.resources) {
    lines.push({ code: r.code, label: r.label, amount: r.amount, available: props.availableByResourceId?.[r.resourceId] });
  }
  return lines;
});
const missingPrereqs = computed<PrereqLine[]>(() =>
  n.value.prerequisites.filter(p => !p.satisfied).map(p => ({
    kind: 'research', label: p.requiredName, currentLevel: p.currentLevel, requiredLevel: p.minLevel, satisfied: false,
  })),
);
</script>

<template>
  <div class="rt-tip">
    <header class="rt-tip-head">
      <img
        v-if="n.imageCode"
        :src="`/images/thumb/192/${imgFolder}/${n.imageCode}.png`"
        class="rt-tip-img" loading="lazy"
        @error="(e) => { const i = e.target as HTMLImageElement; const full = `/images/${imgFolder}/${n.imageCode}.png`; if (!i.src.endsWith(full)) i.src = full; else i.style.visibility='hidden'; }"
      />
      <div>
        <div class="rt-tip-name">{{ n.name }}</div>
        <div class="rt-tip-lvl text-mono">N{{ n.currentLevel }} / {{ n.maxLevel }}</div>
      </div>
    </header>

    <p v-if="n.description" class="rt-tip-desc">{{ n.description }}</p>

    <!-- Ce que ça améliore -->
    <div v-if="n.effects.length" class="rt-tip-section">
      <span class="rt-tip-title">Améliore</span>
      <ul class="rt-tip-effects">
        <li v-for="e in n.effects" :key="e.key">
          {{ e.label }}
          <span class="text-mono" :class="e.isReduction ? 'rt-tip-reduce' : 'rt-tip-gain'">{{ e.isReduction ? '−' : '+' }}{{ e.basePerLevel }}{{ e.isPercent ? '%' : '' }}</span>/niveau
          <span v-if="n.maxLevel > 1" class="rt-tip-dim">
            ({{ e.isReduction ? '−' : '+' }}{{ e.basePerLevel * n.maxLevel }}{{ e.isPercent ? '%' : '' }} à N{{ n.maxLevel }})
          </span>
        </li>
      </ul>
    </div>

    <!-- Coût du prochain niveau -->
    <div v-if="!maxed && n.nextLevelCost" class="rt-tip-section">
      <span class="rt-tip-title">Coût niveau {{ n.currentLevel + 1 }}
        <span v-if="n.catchUp" class="rt-tip-catchup">rattrapage −50%</span></span>
      <div class="rt-tip-costs">
        <ResourceCostList :lines="costGeneric" mode="required" />
        <span class="rt-tip-cost rt-tip-time">⏱ {{ humanDuration(n.nextLevelCost.seconds) }}</span>
      </div>
    </div>
    <div v-else-if="maxed" class="rt-tip-section rt-tip-dim">Niveau maximum atteint.</div>

    <!-- Courbe par niveau (composant partagé, style bâtiment unifié) -->
    <div v-if="hasCurve" class="rt-tip-section">
      <span class="rt-tip-title">Courbe par niveau</span>
      <LevelUpCurve
        :points="curveData"
        :highlight-level="nextCurveLevel"
        :caption="`×${levelupFactorAtMax.toFixed(2)} à N${n.maxLevel} (+${n.levelupPct}% / niveau)`"
      />
    </div>

    <!-- Ce que ça débloque -->
    <div v-if="hasUnlocks" class="rt-tip-section">
      <span class="rt-tip-title">Débloque</span>
      <div v-if="u.buildings.length" class="rt-tip-unlock">🏗️ {{ u.buildings.map(b => b.label).join(', ') }}</div>
      <div v-if="u.ships.length" class="rt-tip-unlock">🚀 {{ u.ships.map(s => s.label).join(', ') }}</div>
      <div v-if="u.researches.length" class="rt-tip-unlock">🔬 {{ u.researches.map(r => r.label).join(', ') }}</div>
    </div>

    <!-- Bâtiment producteur : nécessite le LABO précis de la recherche à un niveau. -->
    <div v-if="(node.requiredBuildingLevel ?? 0) > 0" class="rt-tip-section">
      <p class="rt-tip-adv" :class="{ 'is-missing': buildingMissing }">
        🔬 Nécessite un <strong>{{ labBuilding.label }} niveau {{ node.requiredBuildingLevel }}</strong><template v-if="buildingMissing"> (niveau actuel : {{ labBuilding.current ?? 0 }})</template>.
      </p>
    </div>

    <!-- Prérequis manquants (composant partagé) -->
    <div v-if="missingPrereqs.length" class="rt-tip-section">
      <PrerequisiteList :items="missingPrereqs" title="Prérequis manquants" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.rt-tip { display: flex; flex-direction: column; gap: 8px; max-width: 320px; }
.rt-tip-head { display: flex; gap: 8px; align-items: center; }
.rt-tip-adv {
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  color: $color-neon-cyan;
  &.is-missing { color: $color-warning; }
}
.rt-tip-img { width: 44px; height: 44px; border-radius: 6px; object-fit: cover; }
.rt-tip-name { font-weight: 600; }
.rt-tip-lvl { font-size: 0.8em; opacity: 0.7; }
.rt-tip-desc { margin: 0; font-size: 0.85em; line-height: 1.35; opacity: 0.9; }
.rt-tip-section { display: flex; flex-direction: column; gap: 4px; }
.rt-tip-title { font-size: 0.7em; text-transform: uppercase; letter-spacing: 0.04em; opacity: 0.6; }
.rt-tip-effects { list-style: none; margin: 0; padding: 0; font-size: 0.85em; display: flex; flex-direction: column; gap: 2px; }
.rt-tip-gain { color: #4ade80; }    // bonus (hausse) = vert
.rt-tip-reduce { color: #38bdf8; }  // réduction (baisse de coût/conso) = cyan
.rt-tip-dim { opacity: 0.6; font-size: 0.8em; }
.rt-tip-catchup { color: #22c55e; font-size: 0.85em; margin-left: 6px; }
.rt-tip-costs { display: flex; flex-wrap: wrap; gap: 6px; }
.rt-tip-cost { display: inline-flex; align-items: center; gap: 3px; font-size: 0.85em;
  img { width: 16px; height: 16px; }
  &.insufficient { color: #f87171; }
}
.rt-tip-time { opacity: 0.8; }
.rt-tip-unlock { font-size: 0.85em; }
.rt-tip-missing { color: #f87171;
  ul { list-style: none; margin: 0; padding: 0; font-size: 0.85em; }
}
</style>
